import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useMemo, useState } from "react";
import { FaCheck } from "react-icons/fa";
import { FiCheckCircle, FiClock, FiEdit2, FiExternalLink, FiFileText, FiXCircle } from "react-icons/fi";
import { RiMore2Fill } from "react-icons/ri";
import dynamic from "next/dynamic";
import Image from "next/image";
import CameraCaptureModal from "@/components/camera-capture-modal";
import AddDoseWrapper from "@/components/dose/add-dose-wrapper";
import { DoseDilutionLabel } from "@/components/dose/dilution-label";
import { DoseDropdown } from "@/components/dose/dose-dropdown";
import RefillFormsFlow from "@/components/forms/refill-forms-dialog";
import SendFormsDialog from "@/components/forms/send-forms-dialog";
import { InHouseGFESection } from "@/components/gfe/in-house-gfe-section";
import DocumentViewDialog from "@/components/patient/documents/document-view-dialog";
import { GFEActionsMenu } from "@/components/patient/gfe-actions-menu";
import { RequestGFEModal } from "@/components/patient/request-gfe-modal";
import { RequestQualiphyGFEModal } from "@/components/patient/request-qualiphy-gfe-modal";
import { ProgressBarLink, useProgressNavigation } from "@/components/progress-bar";
import AttachmentAnnotationModal from "@/components/scribe/attachment-annotation-modal";
import CreateNoteWrapper from "@/components/scribe/create-note-wrapper";
import SignedOff from "@/components/signed-off";
import { GFEOutcomeGroups, GFEOutcomeMiniBar, hasGFEOutcomes } from "@/components/spakinect/gfe-outcome-groups";
import Badge from "@/components/ui/badge";
import Button from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useCarePlansForPatient } from "@/hooks/use-care-plans-for-patient";
import { useLocationClock } from "@/hooks/use-clinic-clock";
import { useAppointmentFormStatusList } from "@/hooks/use-form-status-list";
import { useModule } from "@/hooks/use-module";
import { createFormLink } from "@/lib/create-form-link";
import { generateQRCode } from "@/lib/create-qr-code";
import { formatDoseComponentAmount, getDoseComponents, getDosePrimaryLabel } from "@/lib/dose-components";
import { getEventNoteId, getEventNoteTemplateId, getEventNoteTemplateIds } from "@/lib/event-note-template";
import { formatDate, formatName, formatStaffName } from "@/lib/format-data";
import { GALLERY_IMAGE_COMPRESSION_OPTIONS, compressImages, isVideoFile } from "@/lib/image-compression";
import { PERMISSIONS, canAddPhotos, hasPermission } from "@/lib/permissions";
import { applyStudioToNewPhoto } from "@/lib/photo-studio/studio-photo";
import { PHOTO_PERMISSION_DENIED_TOAST } from "@/lib/photo-upload-errors";
import { adjustTimeLabel, encodeServiceAdjustMinutes, rebookingDueDate, rebookingDueDay } from "@/lib/rebooking-defaults";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";
import { Annotation } from "@/types/annotation";
import { DocumentSummary } from "@/types/document";
import { AppointmentChecklistType as ChecklistType } from "@/types/enums/appointment";
import { GFEStatus } from "@/types/enums/spakinect";
import { UserType } from "@/types/enums/user-type";
import { AppointmentChecklistItem, EventDetail } from "@/types/event";
import { FormTiny } from "@/types/forms/form";
import { NoteAttachment } from "@/types/notes";
import { appointmentDayLabel } from "./appointment-day-label";
import { ChecklistPaymentBenefits } from "./checklist-payment-benefits";
import { DefaultMeasurementChecklistAction } from "./default-measurement-checklist-action";
import MeasurementChecklistAction from "./measurement-checklist-action";
import { formQueryOptions } from "@/api/forms/form-query";
import { serviceQueryOptions } from "@/api/provider/admin/service-query";
import { useAnnotationMutation, useSignedUrlUploadMutation } from "@/api/provider/scribe/note-mutation";
import { useCaptureProfilePictureMutation } from "@/api/provider/user/patient/patient-mutation";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
import { spakinectQueryOptions } from "@/api/spakinect/gfe";
import { selectPatientSidebar } from "@/features/sidebar/patient-sidebar";
import { useUpdateAppointmentChecklistMutation } from "@/query/check-in/calendar/event-query";
import { qualiphyQueryOptions } from "@/query/integrations/qualiphy";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
const SubmissionsDrawer = dynamic(() => import("@/components/form/submissions-drawer"), {
  ssr: false
});
interface TakePictureActionProps {
  patientId: string;
  appointmentId: string;
  disabled?: boolean;
  onUploaded?: () => void | Promise<void>;
  className?: string;
}
const TakePictureAction = ({
  patientId,
  appointmentId,
  disabled,
  onUploaded,
  className
}: TakePictureActionProps) => {
  const {
    toast
  } = useToast();
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // Photos captured this session, shown as thumbnails so the user can annotate
  // any of them on demand (rather than being marched through an editor per
  // shot). They also live in the patient gallery for later editing.
  const [capturedPhotos, setCapturedPhotos] = useState<NoteAttachment[]>([]);
  const [annotatingAttachment, setAnnotatingAttachment] = useState<NoteAttachment | null>(null);
  const signedUrlUploadMutation = useSignedUrlUploadMutation(patientId, null);
  const updateAnnotationsMutation = useAnnotationMutation(patientId, null);
  // Annotations save through a note-write route, which Add Photos alone doesn't grant.
  const {
    currentUser
  } = useUser();
  const canAnnotate = hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE);
  const handleCapture = useCallback(async (image: Blob | File, extras?: {
    studioMask?: Blob;
  }) => {
    if (!patientId) return;
    const file = image instanceof File ? image : new File([image], `photo-${Date.now()}.jpg`, {
      type: "image/jpeg"
    });
    const isVideo = isVideoFile(file);
    setIsProcessing(true);
    try {
      const filesToUpload: File[] = isVideo ? [file] : (await compressImages([file], GALLERY_IMAGE_COMPRESSION_OPTIONS)).map(r => r.file);
      const attachments = await signedUrlUploadMutation.mutateAsync({
        files: filesToUpload
      });
      if (attachments.length === 0) {
        throw new Error("No attachments were saved (file may be too large).");
      }
      // Await the checklist-completion callback so we don't claim success
      // before the checklist item is actually marked complete.
      await onUploaded?.();
      toast({
        title: isVideo ? "Video uploaded" : "Photo uploaded",
        description: "Saved to the patient's gallery.",
        variant: "success"
      });
      // Track images (not videos) as annotatable thumbnails. The upload
      // response only populates `id`, but AttachmentAnnotationModal reads
      // `attachmentId` for transforms/history, so mirror it.
      const uploaded = attachments[0];
      // Studio capture: store the cutout and backdrop next to the original.
      if (!isVideo && uploaded && extras?.studioMask) {
        void applyStudioToNewPhoto(uploaded, extras.studioMask);
      }
      if (!isVideo && uploaded) {
        setCapturedPhotos(prev => [...prev, {
          ...uploaded,
          attachmentId: uploaded.attachmentId || uploaded.id
        }]);
      }
    } catch (error) {
      console.error("Failed to upload checklist photo:", error);
      // Rethrow so CameraCaptureModal reports the failure itself; emitting
      // an additional toast would be redundant.
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [patientId, signedUrlUploadMutation, onUploaded, toast]);
  const handleSaveAnnotations = useCallback(async (annotations: Annotation[], baseline: Annotation[]) => {
    if (!annotatingAttachment) return;
    const attachmentId = annotatingAttachment.attachmentId || annotatingAttachment.id;
    if (!attachmentId) return;
    try {
      await updateAnnotationsMutation.mutateAsync({
        attachmentId,
        annotations,
        baseline
      });
      // Keep the thumbnail's annotations in sync so re-opening the editor
      // shows the latest state.
      setCapturedPhotos(prev_0 => prev_0.map(p => p.id === annotatingAttachment.id ? {
        ...p,
        annotations
      } : p));
      setAnnotatingAttachment(null);
    } catch (error_0) {
      console.error("Failed to save annotations:", error_0);
    }
  }, [annotatingAttachment, updateAnnotationsMutation]);
  return <div className={cx("flex flex-col gap-2", className)}>
      <CameraCaptureModal open={cameraModalOpen} onOpenChange={setCameraModalOpen} onCapture={handleCapture} permissionDeniedToast={PHOTO_PERMISSION_DENIED_TOAST} disabled={disabled} patientId={patientId} allowMultiple studioCapture allowVideo>
        <Button variant="secondary" size="sm" onClick={() => setCameraModalOpen(true)} disabled={disabled} isLoading={isProcessing} className="w-fit">
          Take Pictures
        </Button>
      </CameraCaptureModal>
      {capturedPhotos.length > 0 && <div className="flex flex-wrap gap-2">
          {capturedPhotos.map(photo => <div key={photo.id} className="relative size-14 overflow-hidden rounded-md border border-border">
              <Image src={photo.thumbnailSignedUrl || photo.signedUrl || photo.thumbnailUrl || photo.url || ""} alt="Captured photo" width={56} height={56} unoptimized className="size-full object-cover" />
              {canAnnotate && <button type="button" onClick={() => setAnnotatingAttachment(photo)} aria-label="Annotate photo" className="absolute right-0.5 top-0.5 rounded bg-background/80 p-1 text-foreground shadow-sm hover:bg-background">
                  <FiEdit2 className="size-3" />
                </button>}
            </div>)}
        </div>}
      <AttachmentAnnotationModal key={annotatingAttachment?.id} open={!!annotatingAttachment} onOpenChange={open => {
      if (!open) setAnnotatingAttachment(null);
    }} attachment={annotatingAttachment} patientId={patientId || undefined} appointmentId={appointmentId} initialAnnotations={annotatingAttachment?.annotations || []} onSave={handleSaveAnnotations} isSaving={updateAnnotationsMutation.isPending} />
    </div>;
};
interface TakeProfilePhotoActionProps {
  patientId: string;
  hasProfilePhoto: boolean;
}
const TakeProfilePhotoAction = t0 => {
  const $ = _c(14);
  const {
    patientId,
    hasProfilePhoto
  } = t0;
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const captureProfilePictureMutation = useCaptureProfilePictureMutation();
  let t1;
  if ($[0] !== captureProfilePictureMutation || $[1] !== patientId) {
    t1 = async image => {
      await captureProfilePictureMutation.mutateAsync({
        patientId,
        image
      });
    };
    $[0] = captureProfilePictureMutation;
    $[1] = patientId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleCapture = t1;
  const t2 = !patientId;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setCameraModalOpen(true);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = !patientId;
  const t5 = hasProfilePhoto ? "Retake Profile Photo" : "Take Profile Photo";
  let t6;
  if ($[4] !== captureProfilePictureMutation.isPending || $[5] !== t4 || $[6] !== t5) {
    t6 = <Button variant="secondary" size="sm" onClick={t3} disabled={t4} isLoading={captureProfilePictureMutation.isPending}>{t5}</Button>;
    $[4] = captureProfilePictureMutation.isPending;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== cameraModalOpen || $[9] !== handleCapture || $[10] !== patientId || $[11] !== t2 || $[12] !== t6) {
    t7 = <CameraCaptureModal open={cameraModalOpen} onOpenChange={setCameraModalOpen} onCapture={handleCapture} title="Take Profile Photo" disabled={t2} patientId={patientId}>{t6}</CameraCaptureModal>;
    $[8] = cameraModalOpen;
    $[9] = handleCapture;
    $[10] = patientId;
    $[11] = t2;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};
interface ChecklistItemActionProps {
  item: AppointmentChecklistItem;
  className?: string;
  event: EventDetail;
  handleViewNote?: (eventId?: string, noteId?: string) => void;
  patientId?: string;
  servicesById?: Map<string, {
    allowLastItemCompletion?: boolean;
  }>;
  canUpdateItem?: boolean;
  onPermissionDenied?: () => void;
}
const ChecklistItemAction = React.memo(({
  item,
  className,
  event,
  handleViewNote,
  patientId: propPatientId,
  servicesById,
  canUpdateItem = true,
  onPermissionDenied
}: ChecklistItemActionProps) => {
  const {
    patientTiny
  } = useRightSidebarStore(selectPatientSidebar);
  const {
    tenant
  } = useTenant();
  const {
    currentUser
  } = useUser();
  const {
    toast
  } = useToast();
  const patientId = patientTiny?.id || propPatientId || "";
  const {
    data: patient
  } = useQuery({
    ...patientQueryOptions.detail(patientId),
    enabled: !!patientId && item.type !== ChecklistType.FILLED_FORM
  });
  const checklistPatient = patient ?? patientTiny ?? event.attendees?.find(attendee => attendee.user.type === UserType.PATIENT)?.user;
  const {
    data: formSubmissions
  } = useQuery({
    ...formQueryOptions.submissions(patientId),
    enabled: !!patientId
  });
  const addMedication = (_m: any) => {};
  const updateChecklistMutation = useUpdateAppointmentChecklistMutation(servicesById);

  // Recommended follow-ups arrive as ids. Both lists are already cached by
  // the calendar, so resolving the names here costs nothing.
  const {
    data: allServices = []
  } = useQuery({
    ...serviceQueryOptions.list(),
    enabled: item.type === ChecklistType.REBOOK
  });
  const serviceNameById = useMemo(() => new Map(allServices.map(service => [service.id, service.name])), [allServices]);
  // showArchived so a follow-up stays credited after its author is archived.
  // Rebooking is offered at this appointment's location, so its due day is
  // counted on that location's clock.
  const eventClock = useLocationClock(event.location?.id);
  const eventTimeZone = eventClock.timeZone;
  const {
    navigateWithProgress
  } = useProgressNavigation();

  // Care plan query
  const {
    data: carePlans,
    isLoading: loadingCarePlans,
    isError: carePlansError
  } = useCarePlansForPatient(patientId, {
    enabled: !!patientId && item.type === ChecklistType.CARE_PLAN
  });

  // Completed form submission opened for reading from the required-forms list.
  // Gated on the same permission the patient Forms section uses, since the
  // drawer reads submitted answers via /forms/{id}/submissions.
  const [viewingSubmission, setViewingSubmission] = useState<{
    form: FormTiny;
    answeredFormId: string;
  } | null>(null);
  const canViewFormSubmissions = hasPermission(currentUser, PERMISSIONS.FORMS_SUBMISSIONS_READ);
  // Filling a form again archives the current copy, which is what this grants.
  // The forms step reopening follows from the archive, as it does after
  // Archive Submission, so it needs no permission of its own.
  const canRefillForms = hasPermission(currentUser, PERMISSIONS.FORMS_SUBMISSIONS_WRITE);
  const [isRefillOpen, setIsRefillOpen] = useState(false);

  // GFE-related state and queries
  const [isGFEModalOpen, setIsGFEModalOpen] = useState(false);
  const [isQualiphyGFEModalOpen, setIsQualiphyGFEModalOpen] = useState(false);
  // Document opened from a GFE outcome popover (one open at a time across rows).
  const [gfeDocToView, setGfeDocToView] = useState<DocumentSummary | null>(null);
  const hasQualiphyModule = useModule("QUALIPHY");
  const hasInHouseGfeModule = useModule("IN_HOUSE_GFE");
  const hasSpaKinectModule = useModule("SPAKINECT");
  // Past GFEs from either provider load whatever the modules say, so a clinic
  // that switched providers still sees what is on file. The modules only
  // decide who can be asked for a new one.
  const {
    data: gfeRecords,
    isLoading: loadingGFEs
  } = useQuery({
    ...spakinectQueryOptions.patientGfes(patientId),
    enabled: !!patientId && item.type === ChecklistType.REQUEST_GFE
  });
  const {
    data: qualiphyGfes,
    isLoading: loadingQualiphyGFEs
  } = useQuery({
    ...qualiphyQueryOptions.patientGfes(patientId),
    enabled: !!patientId && item.type === ChecklistType.REQUEST_GFE
  });
  const {
    items: formStatusList,
    isCoverageLoading
  } = useAppointmentFormStatusList(event, patientId, formSubmissions);
  const appointmentFormIds = useMemo(() => formStatusList.map(f => f.form.id), [formStatusList]);
  const pendingFormIds = useMemo(() => {
    const pending = formStatusList.filter(f_0 => !f_0.isCompleted).map(f_1 => f_1.form.id);
    return pending.length > 0 ? pending : appointmentFormIds;
  }, [formStatusList, appointmentFormIds]);
  const todaysDoses_0 = useMemo(() => {
    if (!patient?.medications || patient.medications.length === 0) {
      return [];
    }

    // Doses on the appointment's clinic day.
    const clock = eventClock;
    const eventDay = clock.dayOf(event.start);
    const todaysDoses = patient.medications.filter(medication => {
      if (!medication.provider && !medication.providerId) return false;
      return clock.dayOf(medication.doseDate || medication.createdDate) === eventDay;
    });

    // Sort by dose date (or created date), most recent first
    return [...todaysDoses].sort((a, b) => {
      const dateA = new Date(a.doseDate || a.createdDate).getTime();
      const dateB = new Date(b.doseDate || b.createdDate).getTime();
      return dateB - dateA;
    });
  }, [patient?.medications, event.start, eventClock]);
  const handleChecklistItemChange = useCallback(async (itemId: string, checked: boolean, serviceId?: string) => {
    if (!canUpdateItem) {
      onPermissionDenied?.();
      return;
    }
    const optimisticCompletedByUser = item.type === ChecklistType.FILLED_FORM ? checklistPatient : currentUser ?? undefined;
    await updateChecklistMutation.mutateAsync({
      appointmentChecklistId: itemId,
      eventId: event.id,
      serviceId: serviceId,
      completed: checked,
      optimisticCompletedByUser,
      source: "checklist_item_action"
    });
  }, [canUpdateItem, onPermissionDenied, item.type, checklistPatient, currentUser, updateChecklistMutation, event.id]);
  const handleGenerateQRCode = useCallback(async () => {
    if (!tenant?.subDomain) {
      toast({
        title: "Error",
        description: "Unable to determine tenant subdomain.",
        variant: "error"
      });
      return;
    }
    if (formStatusList.length === 0) {
      toast({
        title: "No Forms Available",
        description: "There are no forms available to generate a QR code for.",
        variant: "error"
      });
      return;
    }
    try {
      const patientName = formatName(`${event.attendees.find(a_0 => a_0.user.type === UserType.PATIENT)?.user.firstName}_${event.attendees.find(a_1 => a_1.user.type === UserType.PATIENT)?.user.lastName}`, undefined);
      // Use the unified appointment forms URL
      const appointmentFormsUrl = createFormLink(tenant.subDomain, event.id);
      await generateQRCode(appointmentFormsUrl, `${patientName}_Appointment_Forms_QR`);
      toast({
        title: "Success",
        description: `QR code generated and downloaded for appointment forms.`,
        variant: "success"
      });
    } catch (error) {
      console.error("Failed to generate QR code:", error);
      toast({
        title: "Error",
        description: "Failed to generate QR code. Please try again.",
        variant: "error"
      });
    }
  }, [event.attendees, formStatusList, tenant?.subDomain, toast]);

  // Handle direct navigation to appointment forms (grouped URL)
  const handleViewForms = useCallback(() => {
    if (!tenant?.subDomain) {
      toast({
        title: "Error",
        description: "Unable to determine tenant subdomain.",
        variant: "error"
      });
      return;
    }
    try {
      // Use the unified appointment forms URL
      const appointmentFormsUrl_0 = createFormLink(tenant.subDomain, event.id);
      window.open(appointmentFormsUrl_0, "_blank", "noopener,noreferrer");
      toast({
        title: "Forms Opened",
        description: "Appointment forms opened in new tab.",
        variant: "success"
      });
    } catch (error_0) {
      console.error("Failed to open forms:", error_0);
      toast({
        title: "Error",
        description: "Failed to open forms. Please try again.",
        variant: "error"
      });
    }
  }, [tenant?.subDomain, toast]);

  // GFE helper functions
  const getGFEStatusVariant = useCallback((status: GFEStatus | null) => {
    if (status === GFEStatus.APPROVED || status === GFEStatus.COMPLETED || status === GFEStatus.COMPLETED_IN_CLINIC) return "success";
    if (status === GFEStatus.REJECTED) return "error";
    if (status === GFEStatus.SCHEDULED || status === GFEStatus.INTAKE || status === GFEStatus.PROCESSING || status === GFEStatus.PENDING_REVIEW) return "warning";
    if (status === GFEStatus.CANCELLED || status === GFEStatus.CANCELLED_BY_CLIENT || status === GFEStatus.PROVIDER_CANCELLED) return "neutral";
    return "neutral";
  }, []);
  const getGFEStatusIcon = useCallback((status_0: GFEStatus | null) => {
    if (status_0 === GFEStatus.SCHEDULED || status_0 === GFEStatus.INTAKE || status_0 === GFEStatus.PROCESSING || status_0 === GFEStatus.PENDING_REVIEW) {
      return <FiClock className="h-3 w-3" />;
    }
    if (status_0 === GFEStatus.APPROVED || status_0 === GFEStatus.COMPLETED || status_0 === GFEStatus.COMPLETED_IN_CLINIC) {
      return <FiCheckCircle className="h-3 w-3" />;
    }
    if (status_0 === GFEStatus.REJECTED || status_0 === GFEStatus.CANCELLED || status_0 === GFEStatus.CANCELLED_BY_CLIENT || status_0 === GFEStatus.PROVIDER_CANCELLED) {
      return <FiXCircle className="h-3 w-3" />;
    }
    return null;
  }, []);
  if (!event.attendees) return "";
  switch (item.type) {
    case ChecklistType.FILLED_FORM:
      {
        const completedCount = formStatusList.filter(item_0 => item_0.isCompleted).length;
        const pendingCount = formStatusList.filter(item_1 => !item_1.isCompleted).length;
        return <div className="flex w-full flex-col">
            <div className="my-1 flex w-full flex-col rounded-md border px-3 py-2">
              <>
                <p className="text-caption font-medium text-foreground-secondary">
                  Required Forms ({completedCount}/{formStatusList.length}{" "}
                  completed)
                </p>

                <div className="flex flex-col gap-1">
                  {formStatusList.map(formStatus => {
                  const rowContent = <>
                        <div className="flex min-w-0 items-start gap-1.5">
                          {formStatus.isCompleted ? <FaCheck className="mt-0.5 h-2.5 w-2.5 shrink-0 text-green-600" /> : <div className="mt-0.5 h-2.5 w-2.5 shrink-0" />}
                          <div className="min-w-0 text-left">
                            <p className="text-caption text-foreground-secondary">
                              {formStatus.form.name}
                            </p>
                            {formStatus.coveredBy.length > 0 && <p className="text-caption text-foreground-muted">
                                Covered by{" "}
                                {formStatus.coveredBy.map(form => form.name).join(", ")}
                              </p>}
                          </div>
                        </div>
                        {formStatus.isCompleted ? <p className="shrink-0 whitespace-nowrap text-tiny text-green-600">
                            {formStatus.completedDate ? formatDate(formStatus.completedDate) : "Completed"}
                          </p> : <p className="shrink-0 whitespace-nowrap text-tiny text-amber-600">
                            Pending
                          </p>}
                      </>;

                  // Completed forms open their submission for reading; pending
                  // rows have nothing to show yet, so they stay static text.
                  // Without submissions-read permission every row stays static.
                  return formStatus.isCompleted && formStatus.submission && canViewFormSubmissions ? <button key={formStatus.form.id} type="button" onClick={() => setViewingSubmission({
                    form: formStatus.coveredBy[0] ?? formStatus.form,
                    answeredFormId: formStatus.submission!.answeredFormId
                  })} className="-mx-1 flex w-full items-start justify-between gap-2 rounded-sm px-1 py-0.5 text-left transition-colors hover:bg-muted focus:outline-none focus-visible:ring-1 focus-visible:ring-primary">
                        {rowContent}
                      </button> : <div key={formStatus.form.id} className="flex items-start justify-between gap-2">
                        {rowContent}
                      </div>;
                })}
                </div>

                {pendingCount > 0 && <p className="mt-1 text-caption text-muted-foreground">
                    {pendingCount}{" "}
                    {pendingCount === 1 ? <span>{"form"}</span> : <span>{"forms"}</span>}
                    <span> still needed</span>
                  </p>}
              </>
            </div>

            {checklistPatient && <div className="flex items-center gap-1">
                {/* Once the step is complete nothing is pending, so Send would
                    resend forms already done; Fill again in the menu covers that. */}
                {!item.completedDate && <SendFormsDialog patient={checklistPatient} appointmentId={event.id} initialFormIds={pendingFormIds} appointmentFormIds={appointmentFormIds}>
                    <Button variant="secondary" size="sm">
                      Send Forms to Patient
                    </Button>
                  </SendFormsDialog>}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm" className="px-2" aria-label="More form actions" disabled={!(formStatusList.length > 0)}>
                      <RiMore2Fill className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleGenerateQRCode}>
                      Generate QR Code
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleViewForms}>
                      View Forms
                    </DropdownMenuItem>
                    {canRefillForms && completedCount > 0 &&
                // The window keeps the lists it opened with, so wait until
                // older forms' coverage is known or done forms show as needed.
                <DropdownMenuItem onClick={() => setIsRefillOpen(true)} disabled={isCoverageLoading}>
                        Fill again…
                      </DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>}

            {isRefillOpen && checklistPatient && <RefillFormsFlow patient={checklistPatient} forms={formStatusList.filter(formStatus_0 => formStatus_0.isCompleted).map(formStatus_1 => ({
            form: formStatus_1.form,
            completedDate: formStatus_1.completedDate
          }))} pendingForms={formStatusList.filter(formStatus_2 => !formStatus_2.isCompleted).map(formStatus_3 => formStatus_3.form)} eventId={event.id} appointmentFormIds={appointmentFormIds} onClose={() => setIsRefillOpen(false)} />}

            {viewingSubmission && checklistPatient && <SubmissionsDrawer form={viewingSubmission.form} isOpen={!!viewingSubmission} onClose={() => setViewingSubmission(null)} patient={checklistPatient} submission={{
            isCompleted: true
          }} answeredFormId={viewingSubmission.answeredFormId} />}
          </div>;
      }
    case ChecklistType.MEASUREMENT_TAKEN:
      if (!item.measurementFieldId && !item.measurementGroupId) {
        return <DefaultMeasurementChecklistAction item={item} event={event} patientId={patientId} patient={patient} canUpdateItem={canUpdateItem} />;
      }
      return <MeasurementChecklistAction item={item} event={event} patientId={patientId} canUpdateItem={canUpdateItem} />;
    case ChecklistType.DOSE_TAKEN:
      {
        const appointmentDay = appointmentDayLabel(event.start, eventClock);
        return <div className="">
            <div className="my-1 flex flex-col rounded-md border px-3 py-2">
              <p className="text-caption font-medium text-foreground-secondary">
                {appointmentDay ? `Doses administered on ${appointmentDay}` : "Doses Administered Today"}
              </p>
              {todaysDoses_0.length > 0 ? todaysDoses_0.map((dose, index) => <p key={dose.id} className="flex w-full items-center justify-between gap-4 text-caption text-foreground-secondary">
                    <span className="inline-block w-full truncate">
                      {getDosePrimaryLabel(dose)}:{" "}
                      {getDoseComponents(dose).map(formatDoseComponentAmount).join(" + ")}
                      {dose.administrationLocation && <span>{` (${dose.administrationLocation})`}</span>}{" "}
                      <DoseDilutionLabel components={getDoseComponents(dose)} className="ml-1 align-middle" />
                    </span>
                    <div className="flex items-center gap-1">
                      <SignedOff signOff={dose} showLabel={false} iconClassName="text-green-500" />
                      {patient && <DoseDropdown patient={patient} medication={dose} />}
                      {index < todaysDoses_0.length - 1 && <br />}
                    </div>
                  </p>) : <p className="text-caption text-muted-foreground">
                  No doses administered today
                </p>}
            </div>

            {!item.completedDate && <AddDoseWrapper patientId={patientId} patient={patient} eventId={event.id} onSubmit={addMedication}>
                <Button variant="secondary" size="sm">
                  Add Dose
                </Button>
              </AddDoseWrapper>}
          </div>;
      }
    case ChecklistType.REBOOK:
      {
        // Completion comes from the calendar, so once ticked the step needs no
        // action. While it is open, offer Book — prefilled with whatever the
        // provider recommended, when they recommended anything.
        if (item.completedDate) return null;

        // Names resolve from the cached lists; a service that has since been
        // deleted from the catalog simply drops out rather than showing an id.
        const recommended = (item.recommendedServices ?? []).flatMap(followUp => {
          const serviceName = serviceNameById.get(followUp.serviceId);
          return serviceName ? [{
            ...followUp,
            serviceName
          }] : [];
        }).sort((a_2, b_0) => a_2.serviceName.localeCompare(b_0.serviceName));
        const recommendedNoteId = event.noteIds?.[0];
        const bookingParams = new URLSearchParams({
          patientId: patientId ?? ""
        });
        if (recommended.length) {
          bookingParams.set("serviceIds", recommended.map(followUp_0 => followUp_0.serviceId).join(","));
          // All the services go on one booking, so open it on the soonest
          // date any of them is due. A date already past falls back to the
          // form's own default.
          const soonestDue = Math.min(...recommended.map(followUp_1 => rebookingDueDate(event.start, followUp_1.intervalValue, followUp_1.intervalUnit, eventTimeZone).getTime()));
          if (soonestDue > Date.now()) {
            bookingParams.set("start", new Date(soonestDue).toISOString());
          }
        }
        // Time adjustments ride per service, so each resizes its own segment.
        const serviceAdjustMinutes = encodeServiceAdjustMinutes(recommended);
        if (serviceAdjustMinutes) {
          bookingParams.set("serviceAdjustMinutes", serviceAdjustMinutes);
        }
        if (event.location?.id) {
          bookingParams.set("locationId", event.location.id);
        }
        return <div className={cx("flex flex-col gap-2", className)}>
            {recommended.length > 0 && <ul className="flex flex-col gap-0.5">
                {recommended.map(followUp_2 => {
              const suggestedBy = followUp_2.suggestedById ? formatStaffName(followUp_2.suggestedById) : undefined;
              return <li key={followUp_2.serviceId}>
                      <p className="truncate text-caption-strong text-foreground">
                        {followUp_2.serviceName}
                        <span className="ml-1 font-normal text-foreground-muted">
                          {[followUp_2.intervalLabel, followUp_2.adjustMinutes && adjustTimeLabel(followUp_2.adjustMinutes)].filter(Boolean).join(" · ")}
                        </span>
                      </p>
                      {/* The due day counts from this appointment, the same
                          anchor the note's Rebooking answer shows. Credit the
                          provider too: without it the services read as
                          something the front desk chose. */}
                      <p className="truncate text-caption text-foreground-muted">
                        {[`Around ${rebookingDueDay(event.start, followUp_2.intervalValue, followUp_2.intervalUnit, eventTimeZone)}`, suggestedBy && `Suggested by ${suggestedBy}`].filter(Boolean).join(" · ")}
                      </p>
                    </li>;
            })}
              </ul>}
            <div className="flex items-center gap-2">
              {recommendedNoteId && handleViewNote && <Button variant="secondary" className="w-fit px-2 py-1 text-caption" size="sm" onClick={() => handleViewNote(item.eventId, recommendedNoteId)}>
                  View Note
                </Button>}
              <ProgressBarLink href={`/check-in/appointment-form/new?${bookingParams.toString()}`} className="w-fit">
                <Button variant="secondary" size="sm" className="w-fit px-2 py-1 text-caption">
                  {recommended.length ? "Book follow-up" : "Book"}
                </Button>
              </ProgressBarLink>
            </div>
          </div>;
      }
    case ChecklistType.PAYMENT_MADE:
      {
        // What the patient already holds for this visit, so the front desk
        // knows whether checkout redeems a package or sells one.
        const benefits = patientId ? <ChecklistPaymentBenefits patientId={patientId} event={event} /> : null;
        if (!canUpdateItem) {
          return <div className={cx("flex flex-col gap-2", className)}>
              {benefits}
              <Button variant="secondary" size="sm" className="w-fit" onClick={onPermissionDenied}>
                Take Payment
              </Button>
            </div>;
        }

        // Do NOT mark PAYMENT_MADE complete here — the calendar "Payment Received"
        // status must reflect a payment that actually settled, not the click of
        // "Take Payment" (ENG-3410). The backend completes the item once a
        // committed payment fully settles the appointment's charge.
        return <div className={cx("flex flex-col gap-2", className)}>
            {benefits}
            <ProgressBarLink href={`/billing/check-out?patientId=${patientId}${
          // Pin checkout to where the service is being delivered, not the
          // staff member's own current location.
          event.location?.id ? `&locationId=${event.location.id}` : ""}`} className="w-fit">
              <Button variant="secondary" size="sm">
                Take Payment
              </Button>
            </ProgressBarLink>
          </div>;
      }
    case ChecklistType.NOTE_TAKEN:
      {
        // On a visit with several services, open and pre-fill the note for
        // this row's service, or for the user's own service on a shared row.
        const noteTarget = {
          serviceId: item.serviceId,
          providerId: currentUser?.id
        };
        const noteTemplateId = getEventNoteTemplateId(event, noteTarget);
        const noteTemplateIds = getEventNoteTemplateIds(event, noteTarget);
        const existingNoteId_0 = getEventNoteId(event, noteTarget);
        const existingNote = !!existingNoteId_0;
        return <div className="flex items-center gap-2">
            {item.completedDate && existingNote && handleViewNote && <Button variant="secondary" className={cx("w-fit px-2 py-1 text-caption", className)} size="sm" onClick={() => handleViewNote(item.eventId, existingNoteId_0)}>
                View Linked Note
              </Button>}
            {!item.completedDate && existingNote && handleViewNote && <Button variant="secondary" className={cx("w-fit px-2 py-1 text-caption", className)} size="sm" onClick={() => handleViewNote(item.eventId, existingNoteId_0)}>
                Edit Existing Note
              </Button>}
            <CreateNoteWrapper patient={patient} overrideLocationId={event.location?.id} defaultNoteTemplateId={noteTemplateId} suggestedNoteTemplateIds={noteTemplateIds} eventId={item.eventId}>
              <Button variant="secondary" size="sm" className={cx("w-fit px-2 py-1 text-caption", className)}>
                <span>
                  Take{" "}
                  {(!!item.completedDate || existingNote) && <span>{"New"}</span>}
                  <span> Note</span>
                </span>
              </Button>
            </CreateNoteWrapper>
          </div>;
      }
    case ChecklistType.NOTE_SIGNED_OFF:
      {
        const existingNoteId = getEventNoteId(event, {
          serviceId: item.serviceId,
          providerId: currentUser?.id
        });
        if (!existingNoteId || !handleViewNote) return null;
        return <Button variant="secondary" className={cx("w-fit px-2 py-1 text-caption", className)} size="sm" onClick={() => handleViewNote(item.eventId, existingNoteId)}>
            {item.completedDate ? "View Signed Note" : "Open Note to Sign"}
          </Button>;
      }
    case ChecklistType.REQUEST_GFE:
      {
        // A provider's list shows while its module is on, or while the patient
        // has GFEs from it on file. Ordering follows the modules alone, so a
        // clinic that switched providers isn't asked to pick one every time.
        const showQualiphyList = hasQualiphyModule || (qualiphyGfes?.length ?? 0) > 0;
        const showSpaKinectList = hasSpaKinectModule || (gfeRecords?.length ?? 0) > 0;
        const showBothProviders = showQualiphyList && showSpaKinectList;
        const canRequestFromBoth = hasQualiphyModule && hasSpaKinectModule;
        // With In-House GFE on, the vendor lists sit in the verdict's "GFEs on file"
        // list instead of a box each, and only when they hold something.
        const vendorListClass = cx("my-1 flex flex-col", !hasInHouseGfeModule && "rounded-md border px-3 py-2");
        const qualiphyList = showQualiphyList && <div className={vendorListClass}>
            {(showBothProviders || hasInHouseGfeModule) && <p className="mb-1 text-tiny uppercase tracking-wide text-muted-foreground">
                Qualiphy
              </p>}
            {loadingQualiphyGFEs && <p className="text-caption text-muted-foreground">
                Loading GFEs...
              </p>}

            {!loadingQualiphyGFEs && (!qualiphyGfes || qualiphyGfes.length === 0) && <p className="text-caption text-muted-foreground">
                  No Good Faith Exams requested yet
                </p>}

            {!loadingQualiphyGFEs && qualiphyGfes && qualiphyGfes.length > 0 && <>
                  <p className="mb-2 text-caption font-medium text-foreground-secondary">
                    Recent GFEs ({qualiphyGfes.length})
                  </p>

                  <div className="space-y-2">
                    {qualiphyGfes.slice(0, 3).map(gfe => <div key={gfe.id} className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-caption text-foreground-secondary">
                            {gfe.patientExams.map(pe => pe.examTitle || `Exam ${pe.examId}`).join(", ")}
                          </p>
                          <p className="text-tiny text-muted-foreground">
                            {formatDate(gfe.createdDate)}
                          </p>
                        </div>
                        <Badge variant={gfe.examStatus === "Approved" ? "success" : gfe.examStatus === "Rejected" ? "error" : "default"} className="shrink-0">
                          {gfe.examStatus || "Pending"}
                        </Badge>
                      </div>)}
                  </div>

                  {qualiphyGfes.length > 3 && <p className="mt-2 text-tiny text-muted-foreground">
                      +{qualiphyGfes.length - 3} more in patient chart
                    </p>}
                </>}
          </div>;
        const spakinectList = showSpaKinectList && <div className={vendorListClass}>
            {(showBothProviders || hasInHouseGfeModule) && <p className="mb-1 text-tiny uppercase tracking-wide text-muted-foreground">
                Spakinect
              </p>}
            {loadingGFEs && <p className="text-caption text-muted-foreground">
                Loading GFEs...
              </p>}

            {!loadingGFEs && (!gfeRecords || gfeRecords.length === 0) && <p className="text-caption text-muted-foreground">
                No Good Faith Exams requested yet
              </p>}

            {!loadingGFEs && gfeRecords && gfeRecords.length > 0 && <>
                <p className="mb-2 text-caption font-medium text-foreground-secondary">
                  Recent GFEs ({gfeRecords.length})
                </p>

                <div className="space-y-2">
                  {gfeRecords.slice(0, 3).map(gfe_0 => <div key={gfe_0.id} className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-caption text-foreground-secondary">
                          {gfe_0.requestedTreatments.slice(0, 2).join(", ")}
                          {gfe_0.requestedTreatments.length > 2 && <span>{` +${gfe_0.requestedTreatments.length - 2} more`}</span>}
                        </p>
                        <p className="text-tiny text-muted-foreground">
                          {formatDate(gfe_0.createdDate)}
                        </p>
                        {gfe_0.deferredTreatments && gfe_0.deferredTreatments.length > 0 && <p className="mt-0.5 flex items-center gap-1 truncate text-tiny font-medium text-status-error-text">
                              <FiXCircle className="h-3 w-3 shrink-0" aria-hidden />
                              <span className="truncate">
                                {gfe_0.deferredTreatments.slice(0, 2).join(", ")}
                                {gfe_0.deferredTreatments.length > 2 && <span>{` +${gfe_0.deferredTreatments.length - 2} more`}</span>}
                              </span>
                            </p>}
                        {hasGFEOutcomes(gfe_0) && <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" onClick={e => e.stopPropagation()} className="mt-1 flex w-fit items-center rounded transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" aria-label="View GFE treatment outcomes">
                                <GFEOutcomeMiniBar approvedTreatments={gfe_0.approvedTreatments} conditionallyApprovedTreatments={gfe_0.conditionallyApprovedTreatments} deferredTreatments={gfe_0.deferredTreatments} />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-64 space-y-2">
                              <GFEOutcomeGroups approvedTreatments={gfe_0.approvedTreatments} conditionallyApprovedTreatments={gfe_0.conditionallyApprovedTreatments} deferredTreatments={gfe_0.deferredTreatments} />
                              {gfe_0.document && <button type="button" onClick={() => setGfeDocToView(gfe_0.document ?? null)} className="flex items-center gap-1 text-caption text-primary transition-colors hover:underline">
                                  <FiFileText className="h-3.5 w-3.5 shrink-0" />
                                  View GFE document
                                </button>}
                            </PopoverContent>
                          </Popover>}
                      </div>
                      <div className="flex items-center gap-1">
                        {hasSpaKinectModule && gfe_0.visitLink && gfe_0.status === GFEStatus.INTAKE && <button type="button" onClick={() => {
                    if (gfe_0.visitLink) {
                      window.open(gfe_0.visitLink, "_blank", "noopener,noreferrer");
                    }
                  }} className="flex h-5 w-5 items-center justify-center rounded text-foreground-secondary transition-colors hover:bg-muted hover:text-foreground" title="Open Visit Link">
                              <FiExternalLink className="h-3.5 w-3.5 shrink-0" />
                            </button>}
                        <Badge variant={getGFEStatusVariant(gfe_0.status)} className="flex h-5 w-5 shrink-0 items-center justify-center p-0">
                          {getGFEStatusIcon(gfe_0.status)}
                        </Badge>
                        <GFEActionsMenu gfe={gfe_0} readOnly={!hasSpaKinectModule}>
                          <button type="button" onClick={e_0 => e_0.stopPropagation()} className="flex h-5 w-5 items-center justify-center rounded text-foreground-secondary transition-colors hover:bg-muted hover:text-foreground" aria-label="GFE actions">
                            <RiMore2Fill className="h-3.5 w-3.5 shrink-0" />
                          </button>
                        </GFEActionsMenu>
                      </div>
                    </div>)}
                </div>

                {gfeRecords.length > 3 && <p className="mt-2 text-tiny text-muted-foreground">
                    +{gfeRecords.length - 3} more in patient chart
                  </p>}
                <DocumentViewDialog isOpen={!!gfeDocToView} onOpenChange={open => !open && setGfeDocToView(null)} document={gfeDocToView} />
              </>}
          </div>;

        // When scheduling determines an existing valid GFE on file already
        // covers the booked services, the REQUEST_GFE checklist is
        // auto-ticked server-side and `completedByUser` is left empty.
        // Render a clear hint so the cashier/provider knows why it's ticked.
        const isAutoCompleted = !!item.completedDate && !item.completedByUser;
        const qualiphyCount = qualiphyGfes?.length ?? 0;
        const spakinectCount = gfeRecords?.length ?? 0;
        return <div className="flex flex-col">
            {!hasInHouseGfeModule && isAutoCompleted && <p className="mb-1 text-caption text-status-success-text">
                Valid GFE on file — auto-completed.
              </p>}
            {!hasInHouseGfeModule && qualiphyList}
            {!hasInHouseGfeModule && spakinectList}

            {hasInHouseGfeModule ?
          // Verdict first, one "New GFE" menu for every source, and every GFE on
          // file (any source) folded under it.
          <InHouseGFESection className="my-1" patientId={patientId} patient={patient} eventId={item.eventId} locationId={event.location?.id} visitHandled={!!item.completedDate} visitAutoCompleted={isAutoCompleted} otherGfesCount={qualiphyCount + spakinectCount} otherGfes={<>
                    {qualiphyCount > 0 && qualiphyList}
                    {spakinectCount > 0 && spakinectList}
                  </>} vendorRequests={[...(hasQualiphyModule && patient ? [{
            label: "Request via Qualiphy",
            onSelect: () => setIsQualiphyGFEModalOpen(true)
          }] : []), ...(hasSpaKinectModule && patient ? [{
            label: "Request via Spakinect",
            onSelect: () => setIsGFEModalOpen(true)
          }] : [])]} /> : !hasQualiphyModule && !hasSpaKinectModule ?
          // A service's stored checklist can outlive both modules. Say why
          // there's no button rather than render an inert one.
          <p className="text-caption text-muted-foreground">
                Enable Qualiphy or Spakinect in Settings to request a GFE.
              </p> : canRequestFromBoth ? <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" size="sm">
                    Request New GFE
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setIsQualiphyGFEModalOpen(true)}>
                    Request via Qualiphy
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsGFEModalOpen(true)}>
                    Request via Spakinect
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu> : <Button variant="secondary" size="sm" onClick={() => hasQualiphyModule ? setIsQualiphyGFEModalOpen(true) : setIsGFEModalOpen(true)}>
                Request New GFE
              </Button>}

            {patient && hasQualiphyModule && <RequestQualiphyGFEModal open={isQualiphyGFEModalOpen} onOpenChange={setIsQualiphyGFEModalOpen} patientId={patientId} patientEmail={patient.email} patientPhone={patient.phoneNumber} patientDateOfBirth={patient.dateOfBirth ?? undefined} patientState={patient.state ?? undefined} locationState={event.location?.state ?? undefined} />}

            {patient && hasSpaKinectModule && <RequestGFEModal open={isGFEModalOpen} onOpenChange={setIsGFEModalOpen} patientId={patientId} patientEmail={patient.email} patientPhone={patient.phoneNumber} patientDateOfBirth={patient.dateOfBirth ?? undefined} event={event} />}
          </div>;
      }
    case ChecklistType.CARE_PLAN:
      {
        return <div className="flex flex-col">
            <div className="my-1 flex flex-col rounded-md border px-3 py-2">
              {loadingCarePlans && <p className="text-caption text-muted-foreground">
                  Loading care plans...
                </p>}

              {!loadingCarePlans && carePlansError && <p className="text-caption text-status-error-text">
                  Failed to load care plans
                </p>}

              {!loadingCarePlans && !carePlansError && (!carePlans || carePlans.length === 0) && <p className="text-caption text-muted-foreground">
                    No care plans assigned yet
                  </p>}

              {!loadingCarePlans && carePlans && carePlans.length > 0 && <>
                  <p className="text-caption font-medium text-foreground-secondary">
                    Active Care Plans ({carePlans.length})
                  </p>
                  {carePlans.map(cp => {
                const progressPercent = cp.stepCount > 0 ? Math.round(cp.completedStepCount / cp.stepCount * 100) : 0;
                return <div key={cp.id} className="flex items-center justify-between gap-2">
                        <p className="min-w-0 truncate text-caption text-foreground-secondary">
                          {cp.name}
                        </p>
                        <p className="shrink-0 whitespace-nowrap text-tiny text-foreground-secondary">
                          {progressPercent}%
                        </p>
                      </div>;
              })}
                </>}
            </div>

            <Button variant="secondary" size="sm" onClick={() => navigateWithProgress(`/patient/${patientId}/care-plan/new`)}>
              Assign Care Plan
            </Button>
          </div>;
      }
    case ChecklistType.PICTURE_TAKEN:
      if (!canUpdateItem) {
        return <Button variant="secondary" size="sm" className={cx("w-fit", className)} onClick={onPermissionDenied}>
              Take Pictures
            </Button>;
      }
      // Saving the photos hits the gallery upload routes, so it needs that too,
      // and the denial names that permission rather than the checklist one.
      if (!canAddPhotos(currentUser)) {
        return <Button variant="secondary" size="sm" className={cx("w-fit", className)} onClick={() => toast(PHOTO_PERMISSION_DENIED_TOAST)}>
              Take Pictures
            </Button>;
      }
      return <TakePictureAction patientId={patientId} appointmentId={event.id} disabled={!patientId} className={className} onUploaded={async () => {
        if (!item.completedDate) {
          await handleChecklistItemChange(item.id, true, item.serviceId);
        }
      }} />;
    case ChecklistType.PROFILE_PHOTO:
      // Saving the photo hits a profile-write endpoint, so it needs that too.
      if (!canUpdateItem || !hasPermission(currentUser, PERMISSIONS.PATIENTS_PROFILES_WRITE)) {
        return <Button variant="secondary" size="sm" onClick={onPermissionDenied}>
              Take Profile Photo
            </Button>;
      }
      return <TakeProfilePhotoAction patientId={patientId} hasProfilePhoto={!!patient?.profilePictureUrl} />;
    default:
      return null;
  }
});
ChecklistItemAction.displayName = "ChecklistItemAction";
export default ChecklistItemAction;